/**
 * Enhanced Production Logger for Naia Web Player
 * 
 * Provides structured logging with different levels for development and production.
 * Includes error tracking, performance monitoring, and audit trails.
 */

interface LogContext {
  component?: string;
  userId?: string;
  sessionId?: string;
  requestId?: string;
  metadata?: Record<string, any>;
  timestamp?: string;
  environment?: string;
}

interface LogEntry {
  level: LogLevel;
  message: string;
  context: LogContext;
  timestamp: string;
  error?: {
    name: string;
    message: string;
    stack?: string;
    code?: string;
  };
  performance?: {
    duration: number;
    operation: string;
    startTime: number;
    endTime: number;
  };
}

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

interface LoggerConfig {
  level: LogLevel;
  environment: string;
  enableConsole: boolean;
  enableRemoteLogging: boolean;
  sentryDsn?: string;
  apiEndpoint?: string;
  bufferSize: number;
  flushInterval: number;
}

class ProductionLogger {
  private config: LoggerConfig;
  private logBuffer: LogEntry[] = [];
  private flushTimer?: number;
  private performanceMarks: Map<string, number> = new Map();

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = {
      level: this.getLogLevel(),
      environment: this.getEnvironment(),
      enableConsole: this.shouldEnableConsole(),
      enableRemoteLogging: this.shouldEnableRemoteLogging(),
      bufferSize: 100,
      flushInterval: 30000, // 30 seconds
      ...config
    };

    this.setupFlushTimer();
    this.setupErrorHandlers();
  }

  private getLogLevel(): LogLevel {
    const env = this.getEnvironment();
    switch (env) {
      case 'production':
        return 'info';
      case 'staging':
        return 'debug';
      default:
        return 'debug';
    }
  }

  private getEnvironment(): string {
    if (typeof window !== 'undefined') {
      // Client-side
      return (import.meta as any).env?.VITE_ENVIRONMENT || 'development';
    } else {
      // Server-side (Cloudflare Workers)
      return (globalThis as any).ENVIRONMENT || 'development';
    }
  }

  private shouldEnableConsole(): boolean {
    const env = this.getEnvironment();
    return env !== 'production' || typeof window !== 'undefined';
  }

  private shouldEnableRemoteLogging(): boolean {
    return this.getEnvironment() === 'production';
  }

  private setupFlushTimer(): void {
    if (typeof window !== 'undefined') {
      this.flushTimer = window.setInterval(() => {
        this.flush();
      }, this.config.flushInterval);
    }
  }

  private setupErrorHandlers(): void {
    if (typeof window !== 'undefined') {
      // Client-side error handling
      window.addEventListener('error', (event) => {
        this.error('Uncaught error', new Error(event.message), {
          component: 'Global Error Handler',
          metadata: {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno
          }
        });
      });

      window.addEventListener('unhandledrejection', (event) => {
        this.error('Unhandled promise rejection', event.reason, {
          component: 'Global Promise Handler'
        });
      });
    }
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error', 'fatal'];
    const currentLevelIndex = levels.indexOf(this.config.level);
    const messageLevelIndex = levels.indexOf(level);
    return messageLevelIndex >= currentLevelIndex;
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    error?: Error,
    context: LogContext = {}
  ): LogEntry {
    const entry: LogEntry = {
      level,
      message,
      context: {
        ...context,
        timestamp: new Date().toISOString(),
        environment: this.config.environment
      },
      timestamp: new Date().toISOString()
    };

    if (error) {
      entry.error = {
        name: error.name,
        message: error.message,
        stack: error.stack,
        code: (error as any).code
      };
    }

    return entry;
  }

  private logEntry(entry: LogEntry): void {
    if (!this.shouldLog(entry.level)) {
      return;
    }

    // Console logging
    if (this.config.enableConsole) {
      this.logToConsole(entry);
    }

    // Buffer for remote logging
    if (this.config.enableRemoteLogging) {
      this.logBuffer.push(entry);
      
      if (this.logBuffer.length >= this.config.bufferSize) {
        this.flush();
      }
    }

    // Immediate error reporting for critical issues
    if (entry.level === 'error' || entry.level === 'fatal') {
      this.reportError(entry);
    }
  }

  private logToConsole(entry: LogEntry): void {
    const { level, message, context, error } = entry;
    const prefix = `[${entry.timestamp}] [${level.toUpperCase()}]`;
    
    let logArgs: any[] = [prefix, message];
    
    if (context.component) {
      logArgs.push(`[${context.component}]`);
    }
    
    if (context.metadata) {
      logArgs.push('Metadata:', context.metadata);
    }
    
    if (error) {
      logArgs.push('Error:', error);
    }

    switch (level) {
      case 'debug':
        console.debug(...logArgs);
        break;
      case 'info':
        console.info(...logArgs);
        break;
      case 'warn':
        console.warn(...logArgs);
        break;
      case 'error':
      case 'fatal':
        console.error(...logArgs);
        break;
    }
  }

  private async reportError(entry: LogEntry): Promise<void> {
    try {
      // Send to Sentry if configured
      if (this.config.sentryDsn && typeof window !== 'undefined') {
        // Simplified Sentry integration - in real implementation, use Sentry SDK
        const sentryPayload = {
          message: entry.message,
          level: entry.level,
          timestamp: entry.timestamp,
          tags: {
            component: entry.context.component,
            environment: entry.context.environment
          },
          extra: {
            context: entry.context,
            error: entry.error
          }
        };
        
        await fetch(`https://sentry.io/api/projects/[PROJECT_ID]/store/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Sentry-Auth': `Sentry sentry_key=${this.config.sentryDsn}`
          },
          body: JSON.stringify(sentryPayload)
        }).catch(() => {
          // Silent fail for error reporting
        });
      }
    } catch {
      // Silent fail - don't let error reporting break the app
    }
  }

  private async flush(): Promise<void> {
    if (this.logBuffer.length === 0) {
      return;
    }

    const logsToSend = [...this.logBuffer];
    this.logBuffer = [];

    try {
      if (this.config.apiEndpoint) {
        await fetch(this.config.apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            logs: logsToSend,
            environment: this.config.environment,
            timestamp: new Date().toISOString()
          })
        });
      }
    } catch (error) {
      // If remote logging fails, add back to buffer
      this.logBuffer.unshift(...logsToSend);
      
      // Log locally that remote logging failed
      if (this.config.enableConsole) {
        console.warn('Failed to send logs to remote endpoint:', error);
      }
    }
  }

  // Public API methods
  debug(message: string, context: LogContext = {}): void {
    this.logEntry(this.createLogEntry('debug', message, undefined, context));
  }

  info(message: string, context: LogContext = {}): void {
    this.logEntry(this.createLogEntry('info', message, undefined, context));
  }

  warn(message: string, context: LogContext = {}): void {
    this.logEntry(this.createLogEntry('warn', message, undefined, context));
  }

  error(message: string, error?: Error, context: LogContext = {}): void {
    this.logEntry(this.createLogEntry('error', message, error, context));
  }

  fatal(message: string, error?: Error, context: LogContext = {}): void {
    this.logEntry(this.createLogEntry('fatal', message, error, context));
  }

  // Performance monitoring
  startPerformanceTimer(operation: string): void {
    this.performanceMarks.set(operation, performance.now());
  }

  endPerformanceTimer(operation: string, context: LogContext = {}): void {
    const startTime = this.performanceMarks.get(operation);
    if (startTime === undefined) {
      this.warn(`Performance timer not found: ${operation}`, context);
      return;
    }

    const endTime = performance.now();
    const duration = endTime - startTime;
    
    this.performanceMarks.delete(operation);

    const entry = this.createLogEntry('info', `Performance: ${operation}`, undefined, context);
    entry.performance = {
      duration,
      operation,
      startTime,
      endTime
    };

    this.logEntry(entry);

    // Warn for slow operations
    if (duration > 1000) { // > 1 second
      this.warn(`Slow operation detected: ${operation} took ${duration.toFixed(2)}ms`, {
        ...context,
        metadata: { ...context.metadata, duration, operation }
      });
    }
  }

  // Audit logging for user actions
  audit(action: string, userId: string, details: Record<string, any> = {}): void {
    this.info(`Audit: ${action}`, {
      component: 'Audit',
      userId,
      metadata: {
        action,
        details,
        auditTrail: true
      }
    });
  }

  // User interaction tracking
  userInteraction(event: string, element: string, userId?: string): void {
    this.debug(`User interaction: ${event}`, {
      component: 'UserInteraction',
      userId,
      metadata: {
        event,
        element,
        timestamp: new Date().toISOString()
      }
    });
  }

  // API request logging
  apiRequest(method: string, endpoint: string, status: number, duration: number, context: LogContext = {}): void {
    const level = status >= 400 ? 'error' : status >= 300 ? 'warn' : 'info';
    
    this.logEntry(this.createLogEntry(level, `API ${method} ${endpoint}`, undefined, {
      ...context,
      component: 'API',
      metadata: {
        ...context.metadata,
        method,
        endpoint,
        status,
        duration
      }
    }));
  }

  // Cleanup
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flush();
  }
}

// Create singleton instance
const logger = new ProductionLogger();

// Export convenience functions for backward compatibility
export const debug = (message: string, context: LogContext = {}) => logger.debug(message, context);
export const info = (message: string, context: LogContext = {}) => logger.info(message, context);
export const warn = (message: string, context: LogContext = {}) => logger.warn(message, context);
export const error = (message: string, err?: Error, context: LogContext = {}) => logger.error(message, err, context);
export const fatal = (message: string, err?: Error, context: LogContext = {}) => logger.fatal(message, err, context);

// Export performance monitoring
export const startTimer = (operation: string) => logger.startPerformanceTimer(operation);
export const endTimer = (operation: string, context: LogContext = {}) => logger.endPerformanceTimer(operation, context);

// Export specialized logging
export const audit = (action: string, userId: string, details: Record<string, any> = {}) => logger.audit(action, userId, details);
export const userInteraction = (event: string, element: string, userId?: string) => logger.userInteraction(event, element, userId);
export const apiRequest = (method: string, endpoint: string, status: number, duration: number, context: LogContext = {}) => logger.apiRequest(method, endpoint, status, duration, context);

// Export logger instance and types
export { logger, ProductionLogger };
export type { LogContext, LogEntry, LogLevel };

// Backward compatibility
export { logger as default }; 